import { useState, useEffect } from 'react'
import { Trophy, Star, TrendingUp, Calendar } from 'lucide-react'
import { anilistService } from '@/services/anilist'
import { Anime } from '@/types/anime'
import { LoadingSpinner } from '@/components/LoadingSpinner'

const RANKING_TYPES = [
  { id: 'score', label: 'Highest Rated', icon: Star, description: 'Top anime by user ratings' },
  { id: 'popularity', label: 'Most Popular', icon: TrendingUp, description: 'Most watched anime' },
  { id: 'trending', label: 'Trending Now', icon: Trophy, description: 'Currently trending anime' }
]

export default function TopAnime() {
  const [animeList, setAnimeList] = useState<Anime[]>([])
  const [loading, setLoading] = useState(true)
  const [activeRanking, setActiveRanking] = useState('score')
  const [page, setPage] = useState(1)
  const [hasNextPage, setHasNextPage] = useState(true)

  useEffect(() => {
    fetchAnime()
  }, [activeRanking])

  const fetchAnime = async (pageNum = 1) => {
    try {
      setLoading(pageNum === 1)
      
      let results: Anime[] = []
      
      switch (activeRanking) {
        case 'score':
          results = await anilistService.getTopRatedAnime(20, pageNum)
          break
        case 'popularity':
          results = await anilistService.getPopularAnime(20, pageNum)
          break
        case 'trending':
          results = await anilistService.getTrendingAnime(20, pageNum)
          break
      }
      
      if (pageNum === 1) {
        setAnimeList(results)
      } else {
        setAnimeList(prev => [...prev, ...results])
      }
      
      setHasNextPage(results.length === 20)
      setPage(pageNum)
    } catch (error) {
      console.error('Error fetching anime:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadMore = () => {
    if (hasNextPage && !loading) {
      fetchAnime(page + 1)
    }
  }

  const currentRanking = RANKING_TYPES.find(r => r.id === activeRanking)!

  return (
    <div className="min-h-screen bg-dark-400 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            Top Anime Rankings
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Discover the highest-rated and most popular anime according to the community
          </p>
        </div>

        {/* Ranking Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {RANKING_TYPES.map((ranking) => {
            const IconComponent = ranking.icon
            return (
              <button
                key={ranking.id}
                onClick={() => setActiveRanking(ranking.id)}
                className={`flex items-center space-x-3 px-6 py-4 rounded-lg font-medium transition-all duration-200 ${
                  activeRanking === ranking.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <IconComponent className="w-5 h-5" />
                <div className="text-left">
                  <div className="font-semibold">{ranking.label}</div>
                  <div className="text-xs opacity-80">{ranking.description}</div>
                </div>
              </button>
            )
          })}
        </div>

        {/* Current Ranking Info */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <currentRanking.icon className="w-6 h-6 text-blue-400" />
            <h2 className="text-xl font-bold text-white">{currentRanking.label}</h2>
          </div>
          <p className="text-gray-300">{currentRanking.description}</p>
        </div>

        {/* Rankings List */}
        {loading && animeList.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : animeList.length > 0 ? (
          <>
            {/* Top 3 Special Display */}
            {page === 1 && (
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-white mb-6 text-center">
                  Top 3 {currentRanking.label}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {animeList.slice(0, 3).map((anime, index) => (
                    <div
                      key={anime.id}
                      className={`relative p-6 rounded-xl ${
                        index === 0
                          ? 'bg-gradient-to-br from-yellow-500 to-orange-500'
                          : index === 1
                          ? 'bg-gradient-to-br from-gray-400 to-gray-600'
                          : 'bg-gradient-to-br from-amber-600 to-amber-800'
                      } text-white`}
                    >
                      <div className="absolute top-4 right-4">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                          index === 0 ? 'bg-white/20' : 'bg-black/20'
                        }`}>
                          #{index + 1}
                        </div>
                      </div>
                      
                      <div className="pr-16">
                        <h4 className="text-xl font-bold mb-2 line-clamp-2">
                          {anime.title.english || anime.title.romaji}
                        </h4>
                        
                        <div className="flex items-center space-x-4 text-sm opacity-90 mb-3">
                          {anime.averageScore && (
                            <div className="flex items-center space-x-1">
                              <Star className="w-4 h-4 fill-current" />
                              <span>{anime.averageScore}%</span>
                            </div>
                          )}
                          {anime.seasonYear && (
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>{anime.seasonYear}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-1">
                          {anime.genres.slice(0, 2).map((genre) => (
                            <span
                              key={genre}
                              className="px-2 py-1 bg-white/20 rounded text-xs"
                            >
                              {genre}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Full Rankings Grid */}
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white mb-6">
                Complete Rankings
              </h3>
              <p className="text-gray-400 mb-6">
                Showing {animeList.length} anime in {currentRanking.label.toLowerCase()} order
              </p>
            </div>

            {/* Numbered Grid */}
            <div className="space-y-4 mb-8">
              {animeList.map((anime, index) => (
                <div
                  key={anime.id}
                  className="flex items-center space-x-4 bg-gray-800 rounded-lg p-4 hover:bg-gray-700 transition-colors"
                >
                  {/* Rank Number */}
                  <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold">#{index + 1}</span>
                  </div>

                  {/* Anime Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="text-white font-semibold text-lg mb-1 truncate">
                      {anime.title.english || anime.title.romaji}
                    </h4>
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-400">
                      {anime.averageScore && (
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span className="text-yellow-400">{anime.averageScore}%</span>
                        </div>
                      )}
                      {anime.seasonYear && (
                        <span>{anime.seasonYear}</span>
                      )}
                      {anime.episodes && (
                        <span>{anime.episodes} eps</span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-1 mt-2">
                      {anime.genres.slice(0, 3).map((genre) => (
                        <span
                          key={genre}
                          className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs"
                        >
                          {genre}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Popularity Score */}
                  <div className="flex-shrink-0 text-right">
                    <div className="text-gray-400 text-sm">Popularity</div>
                    <div className="text-white font-medium">
                      {anime.popularity.toLocaleString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More */}
            {hasNextPage && (
              <div className="flex justify-center">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No anime found</p>
          </div>
        )}
      </div>
    </div>
  )
}
